import type { SxProps, Theme } from '@mui/material'

export const styles = {
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as SxProps<Theme>,

  searchInputWrapper: (fullWidth: boolean): SxProps<Theme> => ({
    pt: 2,
    pb: 0,
    px: fullWidth ? 0 : 2,
  }),

  searchIcon: {
    color: 'text.secondary',
    fontSize: 20,
  } as SxProps<Theme>,

  clearButton: {
    p: 0.5,
  } as SxProps<Theme>,

  clearIcon: {
    fontSize: 18,
    color: 'text.secondary',
  } as SxProps<Theme>,

  searchTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
    },
  } as SxProps<Theme>,

  askAiChipContainer: {
    mt: 1.5,
    display: 'flex',
    gap: 1,
  } as SxProps<Theme>,

  askAiChip: {
    cursor: 'pointer',
  } as SxProps<Theme>,

  resultsContainer: (fullWidth: boolean): SxProps<Theme> => ({
    flex: 1,
    overflowY: 'auto',
    py: 2,
    px: fullWidth ? 0 : 2,
  }),

  articleDetailWrapper: {
    display: 'flex',
    flexDirection: 'column',
  } as SxProps<Theme>,

  backHeader: {
    pb: 1,
  } as SxProps<Theme>,

  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.5,
    py: 0.5,
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'text.secondary',
    '&:hover': {
      backgroundColor: 'action.hover',
      color: 'text.primary',
    },
  } as SxProps<Theme>,

  backIcon: {
    fontSize: 18,
  } as SxProps<Theme>,

  backText: {
    fontWeight: 500,
    fontSize: '0.8125rem',
  } as SxProps<Theme>,

  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  } as SxProps<Theme>,

  skeletonCard: (theme: Theme): SxProps<Theme> => ({
    p: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: theme.palette.grey[200],
  }),

  skeletonCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
    mb: 0.5,
  } as SxProps<Theme>,

  resultsListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  } as SxProps<Theme>,

  resultsCountText: {
    fontWeight: 600,
    color: 'text.secondary',
  } as SxProps<Theme>,

  resultCard: (theme: Theme): SxProps<Theme> => ({
    p: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: theme.palette.grey[200],
    cursor: 'pointer',
    '&:hover': {
      bgcolor: theme.palette.grey[50],
      borderColor: 'primary.main',
    },
  }),

  resultCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1,
  } as SxProps<Theme>,

  resultTitle: {
    fontWeight: 600,
    mb: 0.5,
    flex: 1,
  } as SxProps<Theme>,

  resultCategoryChip: {
    fontSize: '0.625rem',
    height: '20px',
    flexShrink: 0,
  } as SxProps<Theme>,

  resultSnippet: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  } as SxProps<Theme>,

  noResultsContainer: {
    textAlign: 'center',
    py: 4,
  } as SxProps<Theme>,

  noResultsText: {
    mb: 2,
  } as SxProps<Theme>,

  noResultsChip: {
    cursor: 'pointer',
    color: 'white',
  } as SxProps<Theme>,

  emptyStateContainer: {
    textAlign: 'center',
    py: 3,
  } as SxProps<Theme>,

  emptyStateIcon: (theme: Theme): SxProps<Theme> => ({
    fontSize: 40,
    color: theme.palette.grey[400],
    mb: 1,
  }),

  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 2,
  } as SxProps<Theme>,

  categoryCard: (theme: Theme): SxProps<Theme> => ({
    cursor: 'pointer',
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: theme.palette.grey[200],
    borderRadius: 1.5,
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: 'primary.main',
      bgcolor: theme.palette.grey[50],
    },
  }),

  categoryIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    bgcolor: 'primary.lightest',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  } as SxProps<Theme>,

  categoryIcon: {
    fontSize: 20,
    color: 'primary.main',
  } as SxProps<Theme>,

  categoryTitle: {
    fontWeight: 600,
    color: 'text.primary',
  } as SxProps<Theme>,

  categoryDescription: {
    color: 'text.secondary',
  } as SxProps<Theme>,

  categoryArticleListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  } as SxProps<Theme>,

  backToCategoriesButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.5,
    py: 0.5,
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'text.secondary',
    alignSelf: 'flex-start',
    '&:hover': {
      backgroundColor: 'action.hover',
      color: 'text.primary',
    },
  } as SxProps<Theme>,

  categoryHeading: {
    fontWeight: 700,
    fontSize: '1rem',
  } as SxProps<Theme>,

  categoryLoadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  } as SxProps<Theme>,

  categoryLoadingSkeleton: {
    borderRadius: 1,
  } as SxProps<Theme>,

  categoryEmptyText: {
    py: 4,
    textAlign: 'center',
  } as SxProps<Theme>,

  categoryArticleCard: (theme: Theme): SxProps<Theme> => ({
    p: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: theme.palette.grey[200],
    cursor: 'pointer',
    '&:hover': {
      bgcolor: theme.palette.grey[50],
      borderColor: 'primary.main',
    },
  }),

  categoryArticleCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  } as SxProps<Theme>,

  categoryArticleTitle: {
    fontWeight: 600,
    flex: 1,
  } as SxProps<Theme>,

  categoryArticleChip: {
    fontSize: '0.625rem',
    height: '20px',
    flexShrink: 0,
  } as SxProps<Theme>,
} as const
